/* ThermoAspen Heater Installation Contract v1.1.0
 * Final Stage 1 contract.
 * Defines saved heater-design intent and the resolved heater-installation output
 * that the future Heater System Engine must produce.
 *
 * IMPORTANT:
 * - This asset does NOT select a heater.
 * - This asset does NOT generate geometry.
 * - This asset does NOT modify Building Geometry.
 * - This asset does NOT alter current Sauna Designer behaviour.
 */
(function(W){
'use strict';

var VERSION='1.1.0';
var SCHEMA_VERSION=1;
var UNITS='mm';
var WORLD_COORDINATE_SYSTEM_ID='ti-room-xyz-v1';

function clone(v){return v==null?v:JSON.parse(JSON.stringify(v))}
function obj(v){return !!v && typeof v==='object' && !Array.isArray(v)}
function arr(v){return Array.isArray(v)}
function str(v){return typeof v==='string' && v.length>0}
function num(v){return typeof v==='number' && isFinite(v)}
function bool(v){return typeof v==='boolean'}
function issue(a,code,path,message){a.push({code:code,path:path,message:message})}
function inList(v,list){return list.indexOf(v)>=0}

var ENUMS={
  selectionMode:['auto','manual'],
  protectionMode:['auto','none','manual'],
  floorMode:['auto','none','manual'],
  fuel:['wood','electric'],
  wall:['front','right','back','left'],
  outletPreference:['auto','top','rear'],
  routePreference:['auto','vertical-roof','rear-wall-vertical'],
  routeType:['vertical-roof','rear-wall-vertical'],
  segmentType:['single-wall','insulated-twin-wall'],
  geometryType:['line','cylinder','elbow','box','annulus','mesh-ref'],
  zoneType:[
    'stove-combustible-clearance',
    'flue-combustible-clearance',
    'operational-access',
    'stove-body',
    'protection-body',
    'floor-protection',
    'penetration-reservation'
  ],
  requirementType:[
    'reserve-clear-zone',
    'roof-opening-required',
    'wall-opening-required',
    'structural-adjustment-required',
    'floor-load-check',
    'weathering-detail-required',
    'penetration-fire-protection-required'
  ],
  requirementStatus:['required','resolved','not-applicable'],
  componentCategory:[
    'stove',
    'stove-protection',
    'floor-protection',
    'single-wall-flue',
    'flue-fitting',
    'flue-protection',
    'insulated-chimney',
    'penetration',
    'weathering',
    'terminal'
  ]
};

/* --------------------------------------------------------------------------
   SAVED PROJECT INTENT
   Only user/design decisions belong here.
   Manufacturer dimensions, clearances and flue geometry do NOT belong here.
   -------------------------------------------------------------------------- */

var PROJECT_HEATER_DESIGN_CONTRACT={
  schemaVersion:1,
  example:{
    id:'heater.01',
    fuel:'wood',
    selectionMode:'auto',
    modelId:null,

    placement:{
      wall:'front',
      offsetMm:300,
      rotationDeg:0
    },

    protection:{
      mode:'auto',
      configurationId:null
    },

    floor:{
      mode:'auto',
      systemId:null
    },

    flue:{
      outletPreference:'auto',
      routePreference:'auto',
      flueSystemId:'harvia-whp1500'
    }
  },

  rules:[
    'modelId is null in auto mode until resolved; manual mode requires a valid catalogue modelId.',
    'placement stores intent only; final world-space transform belongs to generated installation output.',
    'protection configuration stores the chosen approved configuration, never copied clearance values.',
    'floor system stores the chosen catalogue system, never copied dimensions.',
    'flue stores outlet/route/system choices only; generated segments, fittings and penetrations are derived data.',
    'manufacturer facts must remain in Heater Catalogue.'
  ]
};

/* --------------------------------------------------------------------------
   GENERATED / RESOLVED INSTALLATION
   This is the common output consumed by Designer, 2D/3D, Construction Drawings,
   Building Geometry accommodation logic and Cutting & Manufacturing > Stove.
   -------------------------------------------------------------------------- */

var RESOLVED_INSTALLATION_CONTRACT={
  schemaVersion:1,
  units:UNITS,
  coordinateSystemId:WORLD_COORDINATE_SYSTEM_ID,

  topLevel:[
    'schemaVersion',
    'heaterId',
    'catalogueVersion',
    'engineVersion',
    'coordinateSystemId',
    'units',
    'selection',
    'assembly',
    'safety',
    'buildingRequirements',
    'componentManifest',
    'installationSequence',
    'validation'
  ],

  rules:{
    singleAuthority:[
      'Stove overall geometry comes from resolved catalogue model data.',
      'Shielding/protection geometry comes from the selected approved protection configuration.',
      'Flue origin comes from the exact selected manufacturer outlet.',
      'Clearance zones come from resolved stove/protection/flue configuration.',
      'Consumers must not recalculate clearances or flue routes.'
    ],

    heaterPriority:[
      'The stove and resolved flue route have priority over conflicting building framing.',
      'A framing conflict is emitted as a building requirement, not as an invalid heater route.',
      'Building Geometry will later adapt rafters, joists, studs, plates, headers and local floor structure according to rules defined separately.',
      'The Heater System Engine defines the reserved route/clear zone; the Building Geometry Engine resolves construction around it.'
    ],

    physicalVsOperational:[
      'Fire-safety clearance zones and operating/service zones are distinct.',
      'A layout must satisfy both before it is considered usable.'
    ],

    penetration:[
      'Penetrations are generated installation objects.',
      'They identify the host surface, centre/axis, required clear opening and protection/weathering components.',
      'Detailed framing of openings is intentionally deferred to Building Geometry rules.'
    ],

    manufacturing:[
      'Heater/flue penetrations are site-cut unless a later manufacturing rule explicitly changes that.',
      'The resolved installation must preserve enough component and geometry data for Cutting & Manufacturing > Stove.'
    ]
  }
};

/* --------------------------------------------------------------------------
   Geometry conventions
   -------------------------------------------------------------------------- */

var GEOMETRY_CONTRACT={
  transform:{
    positionMm:{x:'number',y:'number',z:'number'},
    rotationDeg:{x:'number',y:'number',z:'number'},
    coordinateSystemId:WORLD_COORDINATE_SYSTEM_ID
  },

  point:{x:'number',y:'number',z:'number'},

  aabbMm:{
    min:{x:'number',y:'number',z:'number'},
    max:{x:'number',y:'number',z:'number'}
  },

  segment:{
    id:'string',
    type:'single-wall | insulated-twin-wall',
    componentId:'catalogue installation component id',
    geometry:{
      type:'line | cylinder | elbow',
      startMm:{x:'number',y:'number',z:'number'},
      endMm:{x:'number',y:'number',z:'number'},
      nominalDiameterMm:'number',
      outerDiameterMm:'number|null'
    }
  },

  penetration:{
    id:'string',
    hostType:'roof | wall | ceiling',
    hostRef:'building surface/member reference when available',
    centreMm:{x:'number',y:'number',z:'number'},
    axis:{x:'number',y:'number',z:'number'},
    flueOuterDiameterMm:'number',
    requiredClearOpeningMm:'number|null',
    componentIds:'array of required protection/weathering component ids',
    installationMethod:'site-cut'
  }
};

/* --------------------------------------------------------------------------
   Component-manifest contract
   This is deliberately installation-oriented so the future Stove section can
   show an exploded assembly and component list without reverse engineering
   the flue geometry.
   -------------------------------------------------------------------------- */

var COMPONENT_MANIFEST_CONTRACT={
  item:{
    instanceId:'unique generated instance id',
    catalogueComponentId:'catalogue installation-component id or stove/protection/floor id',
    category:'componentCategory enum',
    quantity:'number',
    suppliedAsPartOf:'catalogue system id|null',
    installOrder:'integer',
    parentInstanceId:'instance id|null',
    geometryRef:'generated geometry id|null',
    procurement:{
      includeInMaterials:'boolean',
      note:'string|null'
    }
  },

  rules:[
    'Each physical installation item receives a stable instanceId.',
    'Kit components remain individually addressable even when supplied as one kit.',
    'The component manifest is the authority for the future Stove component list.',
    'Geometry references link the component list to the 3D/2D installation visual.'
  ]
};

/* --------------------------------------------------------------------------
   Installation sequence contract
   -------------------------------------------------------------------------- */

var INSTALLATION_SEQUENCE_CONTRACT={
  step:{
    id:'string',
    order:'integer',
    title:'short installation step',
    componentInstanceIds:'array',
    geometryRefs:'array',
    buildingRequirementIds:'array',
    notes:'array of strings'
  },

  expectedKinds:[
    'floor-protection',
    'stove-positioning',
    'stove-shielding',
    'single-wall-flue',
    'single-to-insulated-transition',
    'penetration-preparation',
    'insulated-chimney',
    'roof-weathering',
    'terminal',
    'final-clearance-check'
  ]
};

/* --------------------------------------------------------------------------
   Building-requirement contract
   -------------------------------------------------------------------------- */

var BUILDING_REQUIREMENT_CONTRACT={
  item:{
    id:'string',
    type:'requirementType enum',
    status:'required | resolved | not-applicable',
    priority:'heater-system',
    geometryRef:'reserved geometry id',
    hostRef:'building element/surface ref|null',
    affectedMemberIds:'array',
    note:'string|null'
  },

  interpretation:{
    structuralConflict:'emit structural-adjustment-required and preserve heater/flue geometry',
    roofPenetration:'emit roof-opening-required + weathering-detail-required + penetration-fire-protection-required',
    wallPenetration:'emit wall-opening-required + penetration-fire-protection-required',
    floorLoad:'emit floor-load-check when resolved heater installation load requires structural review'
  }
};

/* --------------------------------------------------------------------------
   Validators
   -------------------------------------------------------------------------- */

function validateEnvironment(){
  var errors=[],warnings=[];
  var c=W.ThermoAspenHeaterCatalogue;

  if(!c){
    issue(errors,'HEATER_CATALOGUE_MISSING','ThermoAspenHeaterCatalogue','Heater Catalogue must load before Heater Installation Contract.');
  }else{
    if(typeof c.getModel!=='function')issue(errors,'HEATER_CATALOGUE_API_MISSING','getModel','Catalogue getModel() API is required.');
    if(typeof c.getFlueSystem!=='function')issue(errors,'HEATER_CATALOGUE_API_MISSING','getFlueSystem','Catalogue getFlueSystem() API is required.');
    if(typeof c.getProtectionConfiguration!=='function')issue(errors,'HEATER_CATALOGUE_API_MISSING','getProtectionConfiguration','Catalogue protection configuration API is required.');
    if(typeof c.getInstallationComponent!=='function')issue(errors,'HEATER_CATALOGUE_API_MISSING','getInstallationComponent','Catalogue installation-component API is required.');
    if(c.schemaVersion!==1)issue(warnings,'HEATER_CATALOGUE_SCHEMA_DIFFERENT','schemaVersion','Contract was authored against Heater Catalogue schema v1.');
  }

  return{
    valid:errors.length===0,
    contractVersion:VERSION,
    schemaVersion:SCHEMA_VERSION,
    coordinateSystemId:WORLD_COORDINATE_SYSTEM_ID,
    catalogueDetected:c?true:false,
    catalogueVersion:c&&c.version||null,
    errors:errors,
    warnings:warnings
  };
}

function validateProjectHeaterDesign(h){
  var errors=[],warnings=[],p='project.design.heater';

  if(!obj(h)){
    issue(errors,'HEATER_DESIGN_MISSING',p,'Heater design object is required.');
    return{valid:false,errors:errors,warnings:warnings};
  }

  if(!str(h.id))issue(errors,'HEATER_ID_MISSING',p+'.id','Heater id is required.');

  if(!inList(h.fuel,ENUMS.fuel))
    issue(errors,'HEATER_FUEL_INVALID',p+'.fuel','Fuel must be wood or electric.');

  if(!inList(h.selectionMode,ENUMS.selectionMode))
    issue(errors,'HEATER_SELECTION_MODE_INVALID',p+'.selectionMode','selectionMode must be auto or manual.');

  if(h.selectionMode==='manual'&&!str(h.modelId))
    issue(errors,'HEATER_MODEL_REQUIRED',p+'.modelId','Manual selection requires modelId.');

  if(h.modelId!=null&&W.ThermoAspenHeaterCatalogue&&typeof W.ThermoAspenHeaterCatalogue.getModel==='function'){
    if(!W.ThermoAspenHeaterCatalogue.getModel(h.modelId))
      issue(errors,'HEATER_MODEL_UNKNOWN',p+'.modelId','Selected heater model is not present in Heater Catalogue.');
  }

  if(!obj(h.placement)){
    issue(errors,'HEATER_PLACEMENT_MISSING',p+'.placement','Placement intent is required.');
  }else{
    if(!inList(h.placement.wall,ENUMS.wall))
      issue(errors,'HEATER_WALL_INVALID',p+'.placement.wall','Unknown sauna wall.');
    if(!num(h.placement.offsetMm)||h.placement.offsetMm<0)
      issue(errors,'HEATER_OFFSET_INVALID',p+'.placement.offsetMm','offsetMm must be zero or greater.');
    if(!num(h.placement.rotationDeg))
      issue(errors,'HEATER_ROTATION_INVALID',p+'.placement.rotationDeg','rotationDeg must be numeric.');
  }

  if(!obj(h.protection)||!inList(h.protection.mode,ENUMS.protectionMode))
    issue(errors,'HEATER_PROTECTION_MODE_INVALID',p+'.protection.mode','Protection mode must be auto, none or manual.');

  if(h.protection&&h.protection.mode==='manual'&&!str(h.protection.configurationId))
    issue(errors,'HEATER_PROTECTION_CONFIG_REQUIRED',p+'.protection.configurationId','Manual protection mode requires configurationId.');

  if(!obj(h.floor)||!inList(h.floor.mode,ENUMS.floorMode))
    issue(errors,'HEATER_FLOOR_MODE_INVALID',p+'.floor.mode','Floor mode must be auto, none or manual.');

  if(h.floor&&h.floor.mode==='manual'&&!str(h.floor.systemId))
    issue(errors,'HEATER_FLOOR_SYSTEM_REQUIRED',p+'.floor.systemId','Manual floor mode requires systemId.');

  if(h.fuel==='electric'){/* Electric heater: no chimney/flue intent. */}
  else if(!obj(h.flue)){
    issue(errors,'HEATER_FLUE_INTENT_MISSING',p+'.flue','Flue intent is required.');
  }else{
    if(!inList(h.flue.outletPreference,ENUMS.outletPreference))
      issue(errors,'HEATER_FLUE_OUTLET_PREF_INVALID',p+'.flue.outletPreference','Invalid outlet preference.');
    if(!inList(h.flue.routePreference,ENUMS.routePreference))
      issue(errors,'HEATER_FLUE_ROUTE_PREF_INVALID',p+'.flue.routePreference','Invalid route preference.');
    if(!str(h.flue.flueSystemId))
      issue(errors,'HEATER_FLUE_SYSTEM_REQUIRED',p+'.flue.flueSystemId','flueSystemId is required.');
    else if(W.ThermoAspenHeaterCatalogue&&typeof W.ThermoAspenHeaterCatalogue.getFlueSystem==='function'&&!W.ThermoAspenHeaterCatalogue.getFlueSystem(h.flue.flueSystemId))
      issue(errors,'HEATER_FLUE_SYSTEM_UNKNOWN',p+'.flue.flueSystemId','Selected flue system is not present in Heater Catalogue.');
  }

  return{valid:errors.length===0,errors:errors,warnings:warnings};
}

function validPoint(p){return obj(p)&&num(p.x)&&num(p.y)&&num(p.z)}

function validateResolvedInstallation(r){
  var errors=[],warnings=[],p='resolvedInstallation';

  if(!obj(r)){
    issue(errors,'RESOLVED_INSTALLATION_MISSING',p,'Resolved installation object is required.');
    return{valid:false,errors:errors,warnings:warnings};
  }

  if(r.schemaVersion!==1)issue(errors,'RESOLVED_SCHEMA_INVALID',p+'.schemaVersion','schemaVersion must be 1.');
  if(!str(r.heaterId))issue(errors,'RESOLVED_HEATER_ID_MISSING',p+'.heaterId','heaterId is required.');
  if(!str(r.catalogueVersion))issue(errors,'RESOLVED_CATALOGUE_VERSION_MISSING',p+'.catalogueVersion','catalogueVersion is required.');
  if(!str(r.engineVersion))issue(errors,'RESOLVED_ENGINE_VERSION_MISSING',p+'.engineVersion','engineVersion is required.');
  if(r.coordinateSystemId!==WORLD_COORDINATE_SYSTEM_ID)
    issue(errors,'RESOLVED_COORDINATE_SYSTEM_INVALID',p+'.coordinateSystemId','Resolved installation must use '+WORLD_COORDINATE_SYSTEM_ID+'.');
  if(r.units!==UNITS)issue(errors,'RESOLVED_UNITS_INVALID',p+'.units','Resolved installation units must be mm.');

  if(!obj(r.selection)||!str(r.selection.modelId))
    issue(errors,'RESOLVED_SELECTION_MISSING',p+'.selection','Resolved model selection is required.');

  if(!obj(r.assembly)){
    issue(errors,'RESOLVED_ASSEMBLY_MISSING',p+'.assembly','Assembly is required.');
  }else{
    if(!obj(r.assembly.stove)||!str(r.assembly.stove.modelId))
      issue(errors,'RESOLVED_STOVE_MISSING',p+'.assembly.stove','Resolved stove is required.');
    if(!obj(r.assembly.flue)&&r.selection&&r.selection.fuel==='electric'){ /* Electric has no flue. */ }
    else if(!obj(r.assembly.flue))
      issue(errors,'RESOLVED_FLUE_MISSING',p+'.assembly.flue','Resolved flue assembly is required.');
    else{
      if(!inList(r.assembly.flue.routeType,ENUMS.routeType))
        issue(errors,'RESOLVED_FLUE_ROUTE_INVALID',p+'.assembly.flue.routeType','Unsupported resolved flue route.');
      if(!arr(r.assembly.flue.segments)||r.assembly.flue.segments.length===0)
        issue(errors,'RESOLVED_FLUE_SEGMENTS_MISSING',p+'.assembly.flue.segments','At least one flue segment is required.');
      else{
        r.assembly.flue.segments.forEach(function(s,i){
          var sp=p+'.assembly.flue.segments['+i+']';
          if(!str(s.id))issue(errors,'FLUE_SEGMENT_ID_MISSING',sp+'.id','Flue segment id is required.');
          if(!inList(s.type,ENUMS.segmentType))issue(errors,'FLUE_SEGMENT_TYPE_INVALID',sp+'.type','Unknown flue segment type.');
          if(!str(s.componentId))issue(errors,'FLUE_SEGMENT_COMPONENT_MISSING',sp+'.componentId','Catalogue component id is required.');
          if(!obj(s.geometry)||!validPoint(s.geometry.startMm)||!validPoint(s.geometry.endMm))
            issue(errors,'FLUE_SEGMENT_GEOMETRY_INVALID',sp+'.geometry','Segment start/end world points are required.');
        });
      }
    }
  }

  if(!obj(r.safety)){
    issue(errors,'RESOLVED_SAFETY_MISSING',p+'.safety','Safety geometry is required.');
  }else{
    if(!arr(r.safety.clearanceZones))
      issue(errors,'RESOLVED_CLEARANCE_ZONES_MISSING',p+'.safety.clearanceZones','clearanceZones array is required.');
    if(!arr(r.safety.operationalZones))
      issue(errors,'RESOLVED_OPERATIONAL_ZONES_MISSING',p+'.safety.operationalZones','operationalZones array is required.');
  }

  if(!arr(r.buildingRequirements))
    issue(errors,'RESOLVED_BUILDING_REQUIREMENTS_MISSING',p+'.buildingRequirements','buildingRequirements array is required.');
  else{
    r.buildingRequirements.forEach(function(x,i){
      var xp=p+'.buildingRequirements['+i+']';
      if(!str(x.id))issue(errors,'BUILDING_REQUIREMENT_ID_MISSING',xp+'.id','Requirement id is required.');
      if(!inList(x.type,ENUMS.requirementType))issue(errors,'BUILDING_REQUIREMENT_TYPE_INVALID',xp+'.type','Unknown requirement type.');
      if(x.priority!=='heater-system')issue(errors,'BUILDING_REQUIREMENT_PRIORITY_INVALID',xp+'.priority','Heater requirements must have heater-system priority.');
    });
  }

  if(!arr(r.componentManifest)||r.componentManifest.length===0)
    issue(errors,'RESOLVED_COMPONENT_MANIFEST_MISSING',p+'.componentManifest','Component manifest is required.');
  else{
    var seen={};
    r.componentManifest.forEach(function(x,i){
      var xp=p+'.componentManifest['+i+']';
      if(!str(x.instanceId))issue(errors,'COMPONENT_INSTANCE_ID_MISSING',xp+'.instanceId','instanceId is required.');
      else if(seen[x.instanceId])issue(errors,'COMPONENT_INSTANCE_ID_DUPLICATE',xp+'.instanceId','instanceId must be unique.');
      else seen[x.instanceId]=true;
      if(!str(x.catalogueComponentId))issue(errors,'COMPONENT_CATALOGUE_ID_MISSING',xp+'.catalogueComponentId','catalogueComponentId is required.');
      if(!inList(x.category,ENUMS.componentCategory))issue(errors,'COMPONENT_CATEGORY_INVALID',xp+'.category','Unknown component category.');
      if(!num(x.quantity)||x.quantity<=0)issue(errors,'COMPONENT_QUANTITY_INVALID',xp+'.quantity','quantity must be greater than zero.');
    });
  }

  if(!arr(r.installationSequence)||r.installationSequence.length===0)
    issue(errors,'RESOLVED_INSTALL_SEQUENCE_MISSING',p+'.installationSequence','Installation sequence is required.');

  if(!obj(r.validation))
    issue(errors,'RESOLVED_VALIDATION_MISSING',p+'.validation','Generated result must carry its own engine validation result.');

  return{valid:errors.length===0,errors:errors,warnings:warnings};
}

function makeDefaultProjectHeaterDesign(){
  return clone(PROJECT_HEATER_DESIGN_CONTRACT.example);
}

var API={
  version:VERSION,
  schemaVersion:SCHEMA_VERSION,
  units:UNITS,
  coordinateSystemId:WORLD_COORDINATE_SYSTEM_ID,

  enums:clone(ENUMS),

  getProjectHeaterDesignContract:function(){return clone(PROJECT_HEATER_DESIGN_CONTRACT)},
  getResolvedInstallationContract:function(){return clone(RESOLVED_INSTALLATION_CONTRACT)},
  getGeometryContract:function(){return clone(GEOMETRY_CONTRACT)},
  getComponentManifestContract:function(){return clone(COMPONENT_MANIFEST_CONTRACT)},
  getInstallationSequenceContract:function(){return clone(INSTALLATION_SEQUENCE_CONTRACT)},
  getBuildingRequirementContract:function(){return clone(BUILDING_REQUIREMENT_CONTRACT)},

  makeDefaultProjectHeaterDesign:makeDefaultProjectHeaterDesign,

  validateEnvironment:validateEnvironment,
  validateProjectHeaterDesign:validateProjectHeaterDesign,
  validateResolvedInstallation:validateResolvedInstallation
};

W.ThermoAspenHeaterInstallationContract=API;

try{
  W.dispatchEvent(new CustomEvent('ThermoAspenHeaterInstallationContractReady',{
    detail:{
      version:VERSION,
      schemaVersion:SCHEMA_VERSION,
      coordinateSystemId:WORLD_COORDINATE_SYSTEM_ID,
      environment:validateEnvironment()
    }
  }));
}catch(e){}

})(window);
